import { useLazyQuery, useMutation } from "@apollo/client";
import { compact, groupBy, noop, sortBy } from "lodash";
import { ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import {
  AssessmentDeliveryState,
  AssessmentState,
  AssessmentType,
  ComplianceProgramCalibrationStatus,
  ComplianceProgramPerformanceReportRowFragment,
  GetComplianceProgramAssessmentsQuery,
  GetComplianceProgramAssessmentsQueryVariables,
  SaveAssessmentDeliveryMutation,
  SaveAssessmentDeliveryMutationVariables,
  UnsubmitAssessmentMutation,
  UnsubmitAssessmentMutationVariables,
  UpdateCalibrationStatusMutation,
  UpdateCalibrationStatusMutationVariables,
} from "types/graphql-schema";

import { numberAnonymousReviews } from "@apps/assessments/components/reopen-review-numbers";
import ReopenReviewOptionLabel from "@apps/assessments/components/reopen-review-option-label";
import reopenAssessmentMutation from "@apps/assessments/graphql/reopen-assessment-mutation";
import updateAssessmentDeliveryMutation from "@apps/assessments/graphql/update-assessment-delivery-mutation";
import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import getComplianceProgramAssessmentsQuery from "@apps/reporting/graphql/get-compliance-program-assessments-query";
import updateCalibrationStatusMutation from "@apps/reporting/graphql/update-calibration-status-mutation";
import useLabel from "@apps/use-label/use-label";
import {
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Dropdown from "@components/dropdown/dropdown";
import { removeConsecutiveDividers } from "@components/dropdown/dropdown-helpers";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { assertEdgesNonNull } from "@helpers/helpers";

import {
  getCalibrateConfirmation,
  getCalibrateSuccess,
  getDeliveryStateChangeConfirmation,
  getDeliveryStateChangeSuccess,
  getReopenConfirmation,
  getReopenSuccess,
  getUndoDeliveryConfirmation,
  getUndoDeliverySuccess,
} from "./confirmation-strings";

const PerformanceAssessmentTableRowActions = ({
  reportRow,
  assessmentDeliveryState,
  canUpdateDeliveryPackage,
  canReadAssessmentDelivery,
  hasPreCalibration,
  hasPostCalibration,
  isAdminApprovalDelivery,
  isManagerApprovalDelivery,
  onRowActionConfirm,
  onOpenEmployeeDrawer,
  onOpenChangelogModal,
  onHasActionsChange,
}: {
  reportRow: ComplianceProgramPerformanceReportRowFragment & {
    calibrationGroupName?: string;
  };
  assessmentDeliveryState: AssessmentDeliveryState | null;
  canUpdateDeliveryPackage: boolean;
  canReadAssessmentDelivery: boolean;
  hasPreCalibration: boolean;
  hasPostCalibration: boolean;
  isAdminApprovalDelivery: boolean;
  isManagerApprovalDelivery: boolean;
  onRowActionConfirm: (message: ReactNode) => Promise<boolean>;
  onOpenEmployeeDrawer: () => void;
  onOpenChangelogModal: () => void;
  onHasActionsChange?: (hasActions: boolean) => void;
}) => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const currentUser = currentUserVar();
  const { complianceProgram, complianceProgramId } = useContext(
    PerformanceAssessmentReportContext,
  );

  const target = useMemo(() => reportRow.user, [reportRow.user]);
  const isTargetManager = useMemo(
    () => target.primaryManager?.id === currentUser.id,
    [target, currentUser],
  );

  const [
    getAssessmentsToReopen,
    { data: targetSubmittedAssessmentsData, loading: isLoadingAssessments },
  ] = useLazyQuery<
    GetComplianceProgramAssessmentsQuery,
    GetComplianceProgramAssessmentsQueryVariables
  >(getComplianceProgramAssessmentsQuery, {
    variables: {
      complianceProgramId,
      targetId: target.id,
    },
  });

  const [saveAssessmentDelivery] = useMutation<
    SaveAssessmentDeliveryMutation,
    SaveAssessmentDeliveryMutationVariables
  >(updateAssessmentDeliveryMutation);

  const [updateCalibrationStatus] = useMutation<
    UpdateCalibrationStatusMutation,
    UpdateCalibrationStatusMutationVariables
  >(updateCalibrationStatusMutation);

  const [reopenAssessment] = useMutation<
    UnsubmitAssessmentMutation,
    UnsubmitAssessmentMutationVariables
  >(reopenAssessmentMutation);

  const handleChangeDeliveryState = useCallback(
    async (state: AssessmentDeliveryState) => {
      const confirmMsg = getDeliveryStateChangeConfirmation(state, {
        label,
        targetName: target.name,
        isTargetManager,
      });
      const successMsg = getDeliveryStateChangeSuccess(state, label);

      const confirmation = await onRowActionConfirm(confirmMsg);
      if (confirmation && reportRow.assessment?.assessmentDelivery) {
        saveAssessmentDelivery({
          variables: {
            assessmentDeliveryId: reportRow.assessment.assessmentDelivery.id,
            state,
          },
          onCompleted: () => {
            successNotificationVar({
              title: successMsg,
            });
          },
        });
      }
    },
    [
      onRowActionConfirm,
      reportRow.assessment,
      saveAssessmentDelivery,
      label,
      target,
      isTargetManager,
    ],
  );

  const handleClickNotStarted = useCallback(async () => {
    handleChangeDeliveryState(AssessmentDeliveryState.AwaitingAdminApproval);
  }, [handleChangeDeliveryState]);

  const handleClickApprove = useCallback(async () => {
    handleChangeDeliveryState(AssessmentDeliveryState.Draft);
  }, [handleChangeDeliveryState]);

  const handleClickUnCalibrate = useCallback(async () => {
    updateCalibrationStatus({
      variables: {
        complianceProgramId,
        targetId: target.id,
        status: ComplianceProgramCalibrationStatus.NotStarted,
      },
      onCompleted: () => {
        successNotificationVar({
          title: `${label("review", {
            capitalize: true,
          })} Package marked as Not Calibrated`,
        });
      },
    });
  }, [complianceProgramId, label, target, updateCalibrationStatus]);

  const handleClickCalibrate = useCallback(async () => {
    const confirmMsg = getCalibrateConfirmation({
      label,
      calibrationType: complianceProgram?.calibration,
    });
    const confirmation = await onRowActionConfirm(confirmMsg);
    if (confirmation) {
      updateCalibrationStatus({
        variables: {
          complianceProgramId,
          targetId: target.id,
          status: ComplianceProgramCalibrationStatus.Calibrated,
        },
        onCompleted: () => {
          const successMsg = getCalibrateSuccess({
            label,
            calibrationType: complianceProgram?.calibration,
            targetName: target.name,
          });
          successNotificationVar({
            title: successMsg,
          });
        },
      });
    }
  }, [
    complianceProgram,
    complianceProgramId,
    label,
    onRowActionConfirm,
    target,
    updateCalibrationStatus,
  ]);

  const handleClickDeliver = useCallback(async () => {
    handleChangeDeliveryState(AssessmentDeliveryState.Delivered);
  }, [handleChangeDeliveryState]);

  const handleClickUndoDeliver = useCallback(async () => {
    const confirmMsg = getUndoDeliveryConfirmation(target.name);
    const successMsg = getUndoDeliverySuccess(label);

    const confirmation = await onRowActionConfirm(confirmMsg);
    if (confirmation && reportRow.assessment?.assessmentDelivery) {
      saveAssessmentDelivery({
        variables: {
          assessmentDeliveryId: reportRow.assessment.assessmentDelivery.id,
          state: isAdminApprovalDelivery
            ? AssessmentDeliveryState.AwaitingAdminApproval
            : AssessmentDeliveryState.Draft,
        },
        onCompleted: () => {
          successNotificationVar({
            title: successMsg,
          });
        },
      });
    }
  }, [
    onRowActionConfirm,
    reportRow.assessment,
    saveAssessmentDelivery,
    target,
    label,
    isAdminApprovalDelivery,
  ]);

  const handleReopenAssessment = useCallback(
    async (assessmentId: number) => {
      const confirmMsg = getReopenConfirmation(label);
      const confirmation = await onRowActionConfirm(confirmMsg);
      if (confirmation && reportRow.assessment) {
        reopenAssessment({
          variables: {
            assessmentIds: [assessmentId],
          },
          onCompleted: () => {
            const successMsg = getReopenSuccess(label);
            successNotificationVar({
              title: successMsg,
            });
          },
        });
      }
    },
    [onRowActionConfirm, reportRow.assessment, reopenAssessment, label],
  );

  const reopenSubmenuOptions: DropdownOptionType[] = useMemo(() => {
    if (isLoadingAssessments) {
      return [
        {
          label: "Loading...",
          onClick: noop,
          type: "button",
          disabled: true,
        },
      ];
    }

    const assessments = targetSubmittedAssessmentsData?.complianceProgram
      ?.assessments
      ? assertEdgesNonNull(
          targetSubmittedAssessmentsData.complianceProgram.assessments,
        ).filter((assessment) => assessment.state === AssessmentState.Submitted)
      : [];

    if (assessments.length === 0) {
      return [
        {
          label: `No ${label("review", { pluralize: true, capitalize: true })} to reopen`,
          onClick: noop,
          type: "button",
          disabled: true,
        },
      ];
    }

    // In id order, so the menu lists the anonymous reviews as Peer Review 1, 2, 3.
    const assessmentsByType = groupBy(
      sortBy(assessments, "id"),
      ({ template }) => template.assessmentType,
    );
    const anonymousNumbers = new Map(
      Object.values(assessmentsByType).flatMap((reviews) => [
        ...numberAnonymousReviews(reviews),
      ]),
    );
    const assessmentItemRenderer = (
      assessment: (typeof assessments)[0],
    ): DropdownOptionType => {
      if (assessment.template.assessmentType === AssessmentType.Performance) {
        return {
          label: (
            <div className="flex items-center">
              <div className="mr-2 font-medium">Reopen:</div>
              <div>{`${assessment.responder?.id === assessment.target?.id ? "Self" : "Manager"} ${label("review", { capitalize: true })}`}</div>
            </div>
          ),
          onClick: () => handleReopenAssessment(assessment.id),
          type: "button",
        };
      }
      const assessmentType =
        assessment.template.assessmentType === AssessmentType.Manager
          ? "Upward"
          : "Peer";
      return {
        label: (
          <ReopenReviewOptionLabel
            assessmentType={assessmentType}
            review={assessment}
            submittedDatetime={assessment.submittedDatetime}
            number={anonymousNumbers.get(assessment.id)}
          />
        ),
        onClick: () => handleReopenAssessment(assessment.id),
        type: "button",
      };
    };

    return removeConsecutiveDividers([
      ...(assessmentsByType[AssessmentType.Performance] ?? []).map(
        assessmentItemRenderer,
      ),
      { type: "divider" },
      ...(assessmentsByType[AssessmentType.Peer] ?? []).map(
        assessmentItemRenderer,
      ),
      { type: "divider" },
      ...(assessmentsByType[AssessmentType.Manager] ?? []).map(
        assessmentItemRenderer,
      ),
    ]);
  }, [
    isLoadingAssessments,
    targetSubmittedAssessmentsData,
    label,
    handleReopenAssessment,
  ]);

  const handleReopenSubmenuOpen = useCallback(() => {
    if (!targetSubmittedAssessmentsData && !isLoadingAssessments) {
      getAssessmentsToReopen();
    }
  }, [
    targetSubmittedAssessmentsData,
    isLoadingAssessments,
    getAssessmentsToReopen,
  ]);

  const actionDropdownOptions: DropdownOptionType[] = useMemo(() => {
    if (
      hasPreCalibration &&
      reportRow.canCalibrateAnswer.permission &&
      reportRow.calibrationStatus ===
        ComplianceProgramCalibrationStatus.NotStarted
    ) {
      return [
        {
          label: "Mark as Calibrated",
          onClick: handleClickCalibrate,
          type: "button",
        },
      ];
    }
    return removeConsecutiveDividers(
      compact([
        (isAdminApprovalDelivery || hasPostCalibration) &&
          (canUpdateDeliveryPackage ||
            reportRow.canCalibrateAnswer.permission) && {
            label: "Change Status to...",
            type: "submenu",
            options: compact([
              reportRow.canCalibrateAnswer.permission &&
                reportRow.calibrationStatus ===
                  ComplianceProgramCalibrationStatus.Calibrated && {
                  label: "Not Calibrated",
                  onClick: handleClickUnCalibrate,
                  type: "button",
                },
              reportRow.canCalibrateAnswer.permission &&
                reportRow.calibrationStatus ===
                  ComplianceProgramCalibrationStatus.NotStarted && {
                  label: "Calibrated",
                  onClick: handleClickCalibrate,
                  type: "button",
                },
              canUpdateDeliveryPackage &&
                assessmentDeliveryState !==
                  AssessmentDeliveryState.AwaitingAdminApproval && {
                  label: "Not Approved for Sharing",
                  onClick: handleClickNotStarted,
                  type: "button",
                },
              canUpdateDeliveryPackage &&
                assessmentDeliveryState !== AssessmentDeliveryState.Draft && {
                  label: "Approved for Sharing",
                  onClick: handleClickApprove,
                  type: "button",
                },
            ]),
          },
        { type: "divider" },
        isAdmin &&
          reportRow.assessment && {
            label: `Reopen ${label("review", { capitalize: true })}...`,
            type: "submenu",
            options: reopenSubmenuOptions,
            onMenuChange: (isOpen) => {
              if (isOpen) {
                handleReopenSubmenuOpen();
              }
            },
          },
        { type: "divider" },
        (isAdminApprovalDelivery || isManagerApprovalDelivery) &&
          canUpdateDeliveryPackage &&
          assessmentDeliveryState !== AssessmentDeliveryState.Delivered && {
            label: `Share ${label("review", { capitalize: true })} with Subject`,
            onClick: handleClickDeliver,
            type: "button",
          },
        (isAdminApprovalDelivery || isManagerApprovalDelivery) &&
          canUpdateDeliveryPackage &&
          assessmentDeliveryState === AssessmentDeliveryState.Delivered && {
            label: "Undo Sharing with Subject",
            onClick: handleClickUndoDeliver,
            type: "button",
          },
        {
          type: "divider",
        },
        (hasPreCalibration || hasPostCalibration) &&
          reportRow.assessment && {
            label: `View Change Log`,
            onClick: onOpenChangelogModal,
            type: "button",
          },
        { type: "divider" },
        reportRow.assessment?.assessmentDelivery &&
          canReadAssessmentDelivery && {
            label: `View ${label("review", { capitalize: true })} Package`,
            onClick: onOpenEmployeeDrawer,
            type: "button",
          },
      ]),
    );
  }, [
    isAdminApprovalDelivery,
    canUpdateDeliveryPackage,
    isAdmin,
    reportRow.calibrationStatus,
    reportRow.assessment,
    reportRow.canCalibrateAnswer.permission,
    handleClickUnCalibrate,
    handleClickCalibrate,
    assessmentDeliveryState,
    handleClickNotStarted,
    handleClickApprove,
    isManagerApprovalDelivery,
    label,
    handleClickDeliver,
    handleClickUndoDeliver,
    hasPostCalibration,
    hasPreCalibration,
    onOpenChangelogModal,
    canReadAssessmentDelivery,
    onOpenEmployeeDrawer,
    reopenSubmenuOptions,
    handleReopenSubmenuOpen,
  ]);

  const realOptionsCount = actionDropdownOptions.filter(
    ({ type }) => type !== "divider",
  ).length;

  useEffect(() => {
    onHasActionsChange?.(realOptionsCount > 0);
  }, [realOptionsCount, onHasActionsChange]);

  if (realOptionsCount === 0) {
    return null;
  }

  return (
    <Dropdown
      align="end"
      options={actionDropdownOptions}
      aria-label="Performance assessment report row dropdown"
    />
  );
};

export default PerformanceAssessmentTableRowActions;
